/**
 * Request Center enums (Spec 1 of 7).
 *
 * - Values follow the platform's lowercase-snake_case convention for
 *   EventBridge/wire compatibility.
 * - `RequestState` is intentionally minimal in Phase 1; Spec 2 extends
 *   it with the workflow's additional states.
 */

export enum RequestCategory {
	EMPLOYEES_LIFECYCLE = 'employees_lifecycle',
	ACCESS_IDENTITY = 'access_identity',
	CONTRACTORS_PARTNERS = 'contractors_partners',
	PROJECTS = 'projects',
	INCIDENTS = 'incidents',
}

/**
 * Coarse lifecycle status of a request. Intentionally *not* per-workflow-step —
 * the active step comes from the `request_approvals` / `request_info_gates`
 * rows, not from this column. Keeps `current_state` authoring-independent so
 * the admin workflow editor can reshape chains without changing the DB enum.
 *
 * Vocabulary:
 *   - `submitted`       — landing state from `POST /requests`, workflow hasn't
 *                         stepped yet (or the request type has no workflow)
 *   - `in_progress`     — an approval or execution step is active
 *   - `pending_info`    — an info_gate step is waiting on the submitter
 *   - `approved`        — final approval landed; awaiting Productive ticket
 *                         creation + closure. The `request-center-worker`
 *                         drives `approved → completed` once every linked
 *                         ticket is closed (see spec
 *                         `comments-audit-and-ticket-lifecycle`).
 *   - `completed`       — all linked tickets closed; provisioning done
 *   - `rejected`        — terminal; any step rejected the request
 *   - `cancelled`       — terminal; submitter or admin cancelled from non-final
 *   - `failed`          — terminal; ticket creation failed unrecoverably and
 *                         the worker flipped `approved → failed`
 *
 * Enforced at the service layer (workflow engine) — no DB CHECK constraint,
 * so the admin workflow editor can reshape chains without a schema migration.
 */
export enum RequestState {
	SUBMITTED = 'submitted',
	IN_PROGRESS = 'in_progress',
	PENDING_INFO = 'pending_info',
	APPROVED = 'approved',
	COMPLETED = 'completed',
	REJECTED = 'rejected',
	CANCELLED = 'cancelled',
	FAILED = 'failed',
}

/**
 * Terminal states of a request. Final states cannot be cancelled by the
 * submitter (admin force-cancel still works on `failed` per design D11).
 *
 * `approved` is NON-terminal — the request is waiting on ticket lifecycle
 * before it can flip to `completed` or `failed`.
 */
const TERMINAL_REQUEST_STATES: ReadonlySet<RequestState> = new Set([
	RequestState.COMPLETED,
	RequestState.REJECTED,
	RequestState.CANCELLED,
	RequestState.FAILED,
])

export function isTerminalRequestState(state: RequestState): boolean {
	return TERMINAL_REQUEST_STATES.has(state)
}

/**
 * Disposition of a single `request_approvals` row.
 *
 * `skipped` covers rows closed by admin (or submitter) force-cancel on the
 * parent request — the approval was neither approved nor rejected, but is
 * no longer pending.
 */
export enum ApprovalStatus {
	PENDING = 'pending',
	APPROVED = 'approved',
	REJECTED = 'rejected',
	SKIPPED = 'skipped',
	/**
	 * Used by `request_info_gates` rows withdrawn before the submitter
	 * answered (the approver or an admin clicks "Cancel info request").
	 * `request_approvals` rows never use this state — they're either
	 * approved/rejected/skipped or stay pending.
	 */
	CANCELLED = 'cancelled',
}

/**
 * Events the workflow engine accepts as transition triggers.
 *
 * Values are lowercase snake_case to match the platform's wire/DB
 * convention — these get persisted to `request_state_history.trigger_action`
 * and surface in EventBridge payloads, so the same "lowercase everywhere
 * it's serialised" rule as `RequestState` / `ApprovalStatus` / `EventType`
 * applies. XState doesn't care about case — `machine.send({ type: 'approve' })`
 * works identically to uppercase.
 */
export enum WorkflowAction {
	SUBMIT = 'submit',
	APPROVE = 'approve',
	REJECT = 'reject',
	CANCEL = 'cancel',
	PROVISION = 'provision',
	COMPLETE = 'complete',
	/**
	 * Spec 3 addition. Recorded on `request_state_history.trigger_action`
	 * when `request-center-worker` flips a request `approved → failed` in
	 * response to a terminal `TICKET_REQUEST_FAILED` event.
	 */
	FAIL = 'fail',
}

export enum RequestPriority {
	NORMAL = 'normal',
	HIGH = 'high',
	CRITICAL = 'critical',
}

/**
 * Form field type registry. Rendered by `<DynamicFormRenderer>` (frontend)
 * and validated by the shared form-schema validator (used by both the service
 * on save and the admin UI for inline feedback).
 *
 * `PRIORITY` is the only "magical" type — its submitted value is mirrored
 * into `requests.priority` outside `formData`. See design.md §D6.
 */
export enum FormFieldType {
	TEXT = 'text',
	TEXTAREA = 'textarea',
	NUMBER = 'number',
	EMAIL = 'email',
	DATE = 'date',
	SELECT = 'select',
	MULTISELECT = 'multiselect',
	AUTOCOMPLETE = 'autocomplete',
	AUTOCOMPLETE_MULTI = 'autocomplete_multi',
	CHECKBOX = 'checkbox',
	RADIO = 'radio',
	USER_PICKER = 'user_picker',
	USER_PICKER_MULTI = 'user_picker_multi',
	PROJECT_PICKER = 'project_picker',
	COUNTRY_PICKER = 'country_picker',
	FILE = 'file',
	FILE_MULTI = 'file_multi',
	PRIORITY = 'priority',
	/**
	 * Composite repeating picker: each entry is a `{ provider, accessLevel }`
	 * pair. Renders as a list the user can grow one entry at a time. Stores
	 * `formData[fieldId] = Array<{ provider: string; accessLevel: string }>`.
	 * Audit logs receive the structured array directly.
	 */
	PROVIDER_ACCESS_LIST = 'provider_access_list',
}

/**
 * Allow-listed `service_name` values accepted by `data_sources.service_name`.
 *
 * Defense-in-depth: admin-registered endpoints can only proxy to these four
 * internal services, never an arbitrary URL. Enforced at the Zod layer and
 * reaffirmed at runtime by `DataSourceService.search` switching on service_name
 * to pick the adapter. See design.md §D5.
 */
export const ALLOWED_DATA_SOURCE_SERVICES = [
	'employee-service',
	'client-service',
	'entra-directory-service',
	'request-center-service',
] as const

export type AllowedDataSourceService = (typeof ALLOWED_DATA_SOURCE_SERVICES)[number]
